import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDossier } from '../services/api';
import { useAuth } from '../services/AuthContext';

const TYPO_PRICES = { RAC_I: 145, RAC_C: 200, RAC_F: 200, RAC_P: 285, REPRO_I: 145, REPRO_C: 200, REPRO_F: 200, REPRO_P: 285 };

function calcDevisPrice(dossier) {
  const montant = parseFloat(dossier.montant_devis_forfaitaire) || 0;
  const typo = dossier.typo_raccordement || '';
  // Les RACC suite SAV ont un prix typo à 0
  if (typo.includes('RACC suite SAV')) return (montant / 2.5).toFixed(2);
  // Extraire le code RAC_ ou REPRO_ de la typo (ex: "RAC_I" → 115)
  const match = typo.match(/REPRO_[IC]|RAC_[ICFP]/);
  const racCode = match ? match[0] : typo;
  const typoPrice = TYPO_PRICES[racCode] || 0;
  return ((montant / 2.5) + typoPrice).toFixed(2);
}

const SAV_TYPOS = ['SAV', 'RACC suite SAV (RAC_I)', 'RACC suite SAV (RAC_C)', 'RACC suite SAV (RAC_P)', 'RACC suite SAV (RAC_F)'];

// Extrait l'adresse principale en supprimant les infos télécom après la ville
// Ex: "12 RUE DE LA PAIX 75001 PARIS - Boite aux lettres 3" → "12 RUE DE LA PAIX 75001 PARIS"
function cleanAddressForNav(raw) {
  if (!raw) return '';
  return raw
    .split(/[\n\r]/)[0]   // première ligne seulement
    .split(/ - /)[0]      // avant " - "
    .split(/ – /)[0]      // avant " – " (tiret demi-cadrat)
    .split(/ \/ /)[0]     // avant " / "
    .split(/\s*[;(]/)[0]  // avant ; ou (
    .trim();
}

// Conversion Lambert 93 (EPSG:2154) → WGS84
// Constantes pré-calculées issues de la documentation IGN NTG_71
function lambert93ToLatLng(easting, northing) {
  const e   = 0.08181919084262149; // excentricité première GRS80
  const n   = 0.7256077650532670;  // exposant de la projection
  const C   = 11754255.4261;       // constante de la projection
  const Xs  = 700000.0;            // coordonnée X du pôle
  const Ys  = 12655612.050;        // coordonnée Y du pôle
  const lon0 = 3.0 * Math.PI / 180; // méridien central (3°E)

  const R     = Math.sqrt((easting - Xs) ** 2 + (northing - Ys) ** 2);
  const gamma = Math.atan((easting - Xs) / (Ys - northing));
  const lon   = lon0 + gamma / n;
  const L     = -Math.log(Math.abs(R / C)) / n; // latitude isométrique

  // Convergence itérative latitude géographique ← latitude isométrique
  let phi = 2 * Math.atan(Math.exp(L)) - Math.PI / 2;
  for (let i = 0; i < 12; i++) {
    phi = 2 * Math.atan(
      Math.exp(L) * Math.pow(
        (1 + e * Math.sin(phi)) / (1 - e * Math.sin(phi)),
        e / 2
      )
    ) - Math.PI / 2;
  }

  return {
    lat: parseFloat((phi * 180 / Math.PI).toFixed(6)),
    lng: parseFloat((lon * 180 / Math.PI).toFixed(6)),
  };
}

// Auto-détecte le système (Lambert 93 ou WGS84) et retourne { lat, lng } ou null
function convertGPSToLatLng(xStr, yStr) {
  const x = parseFloat(String(xStr || '').replace(',', '.'));
  const y = parseFloat(String(yStr || '').replace(',', '.'));
  if (isNaN(x) || isNaN(y) || x === 0 || y === 0) return null;

  // Déjà en WGS84 degrés décimaux
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) {
    return { lat: y, lng: x };
  }

  // Lambert 93 — on détermine quel axe est l'easting (100k–1.3M) et le northing (5M–7.5M)
  const exIsEasting = x > 100000 && x < 1300000 && y > 5000000 && y < 7500000;
  const eyIsEasting = y > 100000 && y < 1300000 && x > 5000000 && x < 7500000;

  if (exIsEasting) return lambert93ToLatLng(x, y);
  if (eyIsEasting) return lambert93ToLatLng(y, x);

  return null;
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="info-row">
      <span className="info-key">{label}</span>
      <span className="info-val">{value}</span>
    </div>
  );
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasContent = React.Children.toArray(children).some(child => child !== null && child !== false && child !== undefined);
  if (!hasContent) return null;
  return (
    <div className="section-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: open ? 8 : 0 }} onClick={() => setOpen(o => !o)}>
        <div className="section-label">{title}</div>
        <span style={{ fontSize: 16, color: 'var(--texte-doux)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>›</span>
      </div>
      {open && children}
    </div>
  );
}

const BADGE = {
  A_faire: 'badge-todo', En_cours: 'badge-progress', Termine: 'badge-done',
  Echec: 'badge-fail', CLEARED: 'badge-done', 'OT Planifiée': 'badge-progress',
  'A planifier': 'badge-todo', 'Commande gelée': 'badge-fail', 'CRI non cloturé': 'badge-progress',
};
const LABEL = {
  A_faire: 'À faire', En_cours: 'En cours', Termine: 'Terminé', Echec: 'Échec',
  CLEARED: 'Cleared', 'OT Planifiée': 'Planifiée', 'A planifier': 'À planifier',
  'Commande gelée': 'Gelée', 'CRI non cloturé': 'CRI non clôturé',
};

export default function Dossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    getDossier(id)
      .then(data => setDossier(data.dossier))
      .catch(() => setError('Impossible de charger le dossier'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="app-shell">
      <div className="topbar"><div className="topbar-title">Dossier</div></div>
      <div className="content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ color: 'var(--texte-doux)' }}>Chargement…</div>
      </div>
    </div>
  );

  if (error || !dossier) return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={() => navigate('/planning')}>‹</div>
        <div className="topbar-title">Dossier</div>
      </div>
      <div className="content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{ color: 'var(--danger-texte)', textAlign: 'center' }}>{error || 'Dossier introuvable'}</div>
      </div>
    </div>
  );

  const isSAV = SAV_TYPOS.includes(dossier.typo_raccordement);
  const isVT = dossier.typo_raccordement === 'VT';
  const adresse = [dossier.numero, dossier.rue, dossier.code_postal, dossier.ville].filter(Boolean).join(' ');
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(adresse)}`;
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(adresse)}&navigate=yes`;

  // Navigation PM
  const pmAdresse = cleanAddressForNav(dossier.adresse_pm);
  const pmMapsUrl = pmAdresse ? `https://maps.google.com/?q=${encodeURIComponent(pmAdresse)}` : null;
  const pmWazeUrl = pmAdresse ? `https://waze.com/ul?q=${encodeURIComponent(pmAdresse)}&navigate=yes` : null;

  // Navigation PBO via coordonnées Lambert 93
  const pboCoords = convertGPSToLatLng(dossier.gps_x, dossier.gps_y);
  const pboMapsUrl = pboCoords ? `https://maps.google.com/?q=${pboCoords.lat},${pboCoords.lng}` : null;
  const badgeClass = BADGE[dossier.statut] || 'badge-todo';
  const badgeLabel = LABEL[dossier.statut] || dossier.statut;

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="back-btn" onClick={() => navigate('/planning')}>‹</div>
        <div className="topbar-title" style={{ fontSize: 15 }}>{dossier.ref_commande}</div>
        <div className={`badge ${badgeClass}`}>{badgeLabel}</div>
      </div>

      <div className="content">

        {dossier.devis_forfaitaire && (
          <div style={{ background: 'var(--attention-fond)', border: '1px solid var(--attention-bord)', borderRadius: 10, padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 16 }}>💰</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--attention-texte)', flex: 1 }}>Intervention sous DEVIS — tarification spécifique</span>
            {user?.voir_prix_devis && (
              <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--attention-texte)', background: 'var(--attention-bord)', borderRadius: 8, padding: '2px 10px', whiteSpace: 'nowrap' }}>
                {calcDevisPrice(dossier)} €
              </span>
            )}
          </div>
        )}

        {isSAV && (
          <div style={{ background: 'var(--attention-fond)', border: '1px solid var(--attention-texte)', borderRadius: 10, padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🔧</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--attention-texte)' }}>Intervention SAV — {dossier.typo_raccordement}</span>
          </div>
        )}

        {isVT && (
          <div style={{ background: 'var(--info-fond)', border: '1px solid var(--accent-fonce)', borderRadius: 10, padding: '8px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🔍</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent-fonce)' }}>Visite Technique</span>
          </div>
        )}

        {dossier.pose_de_cpe && (
          <div style={{ background: 'var(--danger-fond)', border: '2px solid var(--danger)', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>📦</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--danger-texte)', letterSpacing: 0.2 }}>Pose d'un CPE à réaliser</span>
          </div>
        )}

        <Section title="Informations principales">
          <InfoRow label="Réf commande" value={dossier.ref_commande} />
          <InfoRow label="Réf Work Order" value={dossier.ref_work_order} />
          <InfoRow label="Client final" value={dossier.client_final} />
          <InfoRow label="Typo raccordement" value={dossier.typo_raccordement} />
          <InfoRow label="Technicien OT" value={dossier.technicien_ot} />
          <InfoRow label="Date intervention" value={dossier.date_intervention} />
          <InfoRow label="Heure" value={dossier.heure_intervention} />
          <InfoRow label="Client opérateur" value={dossier.client_operateur} />
          <InfoRow label="Offre" value={dossier.offre} />
          <InfoRow label="ONT Type" value={dossier.ont_type} />
          <InfoRow label="Adresse" value={adresse} />
          <InfoRow label="Contact site" value={dossier.nom_contact_site} />
          <InfoRow label="Tél fixe" value={dossier.tel_fixe} />
          <InfoRow label="Tél mobile" value={dossier.tel_mobile} />
          <InfoRow label="Commentaire VALEA" value={dossier.commentaire_valea} />
          <InfoRow label="Commentaire COVAGE" value={dossier.commentaire_covage} />
          <InfoRow label="Commentaire client" value={dossier.commentaire_client} />
          <div className="info-row">
            <span className="info-key">Pose de CPE</span>
            {dossier.pose_de_cpe
              ? <span className="info-val" style={{ fontWeight: 800, color: 'var(--danger-texte)' }}>Oui</span>
              : <span className="info-val">Non</span>
            }
          </div>
        </Section>

        {isSAV && dossier.rop_sav && (
          <Section title="Route optique SAV (ROP)">
            <InfoRow label="Réf prise" value={dossier.rop_sav.ref_prise} />
            <InfoRow label="VIA" value={dossier.rop_sav.via} />
            <InfoRow label="Réf PBO" value={dossier.rop_sav.ref_pbo} />
            <InfoRow label="Adresse PBO" value={dossier.rop_sav.adresse_pbo} />
            <InfoRow label="Tube" value={dossier.rop_sav.tube} />
            <InfoRow label="Fibre" value={dossier.rop_sav.fibre} />
            <InfoRow label="Réf PM" value={dossier.rop_sav.ref_pm} />
            <InfoRow label="Adresse PM" value={dossier.rop_sav.adresse_pm} />
            <InfoRow label="Position porte droite" value={dossier.rop_sav.position_porte_droite} />
            <InfoRow label="Position porte gauche" value={dossier.rop_sav.position_porte_gauche} />
            <InfoRow label="Commentaire" value={dossier.rop_sav.commentaire} />
            {(() => {
              const m = (dossier.commentaire_valea || '').match(/SLID de l['']ONT\s*:\s*(.+?)(?:\n|$)/i);
              return m ? <InfoRow label="SLID de l'ONT" value={m[1].trim()} /> : null;
            })()}
          </Section>
        )}

        <Section title="Synthèse technique" defaultOpen={false}>
          <InfoRow label="Réf PTO" value={dossier.ref_pto} />
          <InfoRow label="Code IMB" value={dossier.code_imb} />
          <InfoRow label="N° VIA" value={dossier.n_via} />
          <InfoRow label="OI" value={dossier.oi} />
        </Section>

        {!isSAV && (
          <>
            <Section title="Informations PBO" defaultOpen={false}>
              <InfoRow label="Réf PT PBO" value={dossier.ref_pt_pbo} />
              <InfoRow label="Prise existante" value={dossier.prise_existante} />
              <InfoRow label="Type raccordement" value={dossier.type_raccordement_pbo} />
              <InfoRow label="Infos PBO/PTO" value={dossier.infos_pbo_pto} />
              <InfoRow label="Hauteur PB" value={dossier.hauteur_pb} />
              <InfoRow label="Type PBO" value={dossier.type_pbo} />
              <InfoRow label="Type matériel PBO" value={dossier.type_materiel_pbo} />
              <InfoRow label="Câble PBO" value={dossier.cable_pbo} />
              <InfoRow label="Tube" value={dossier.tube} />
              <InfoRow label="Fibre" value={dossier.fibre} />
              <InfoRow label="Adresse PBO" value={dossier.adresse_pbo} />
              <InfoRow label="GPS X" value={dossier.gps_x} />
              <InfoRow label="GPS Y" value={dossier.gps_y} />
              {pboMapsUrl && (
                <div style={{ marginTop: 10 }}>
                  <a href={pboMapsUrl} target="_blank" rel="noreferrer"
                    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', background: 'var(--info-fond)', color: 'var(--accent)', border: '1px solid var(--info-bord)', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
                    🗺 Ouvrir le PBO dans Maps
                  </a>
                </div>
              )}
            </Section>

            <Section title="Informations PM" defaultOpen={false}>
              <InfoRow label="Réf PM réglementaire" value={dossier.ref_pm} />
              <InfoRow label="N° PT PM" value={dossier.n_pt_pm} />
              <InfoRow label="PM accessible ?" value={dossier.pm_accessible} />
              <InfoRow label="Adresse PM" value={dossier.adresse_pm} />
              <InfoRow label="Position bloc opérateur" value={dossier.position_bloc_operateur} />
              <InfoRow label="Position bloc client" value={dossier.position_bloc_client} />
              {(pmMapsUrl || pmWazeUrl) && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  {pmMapsUrl && (
                    <a href={pmMapsUrl} target="_blank" rel="noreferrer"
                      style={{ textDecoration: 'none', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', background: 'var(--info-fond)', color: 'var(--accent)', border: '1px solid var(--info-bord)', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
                      🗺 Maps
                    </a>
                  )}
                  {pmWazeUrl && (
                    <a href={pmWazeUrl} target="_blank" rel="noreferrer"
                      style={{ textDecoration: 'none', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', background: 'var(--info-fond)', color: 'var(--accent)', border: '1px solid var(--info-bord)', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
                      🔵 Waze
                    </a>
                  )}
                </div>
              )}
            </Section>
          </>
        )}

        <Section title="Informations ONT" defaultOpen={false}>
          <InfoRow label="SLID offre active" value={dossier.slid_active} />
          <InfoRow label="SLID offre passive" value={dossier.slid_passive} />
          <InfoRow label="ONT Type" value={dossier.ont_type_detail} />
        </Section>

        <div style={{ marginTop: 8 }}>
          {isSAV ? (
            <button className="btn btn-primary" style={{ background: 'var(--attention-texte)' }} onClick={() => navigate(`/cr-sav/${dossier.id}`)}>
              Créer le compte-rendu SAV
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => navigate(`/cr/${dossier.id}`)}>
              Créer le compte-rendu
            </button>
          )}

          {isVT && (
            <button className="btn btn-primary" style={{ background: 'var(--accent-fonce)', marginTop: 4 }} onClick={() => navigate(`/cr-vt/${dossier.id}`)}>
              Créer le compte-rendu VT
            </button>
          )}

          <div style={{ display: 'flex', gap: 8, marginBottom: 0 }}>
            <a className="btn btn-secondary" href={mapsUrl} target="_blank" rel="noreferrer"
              style={{ textDecoration: 'none', flex: 1, textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 6 }}>
              🗺 Maps
            </a>
            <a className="btn btn-secondary" href={wazeUrl} target="_blank" rel="noreferrer"
              style={{ textDecoration: 'none', flex: 1, textAlign: 'center', justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 6 }}>
              🔵 Waze
            </a>
          </div>

          {dossier.tel_mobile && (
            <a className="btn btn-secondary" href={`tel:${dossier.tel_mobile}`} style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              📞 Appeler le client
            </a>
          )}
          {!dossier.tel_mobile && dossier.tel_fixe && (
            <a className="btn btn-secondary" href={`tel:${dossier.tel_fixe}`} style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              📞 Appeler le client
            </a>
          )}
        </div>

        <div style={{ height: 16 }} />
      </div>

      <div className="bottomnav">
        <div className="nav-item" onClick={() => navigate('/planning')}><span className="nav-icon">📅</span>Planning</div>
        <div className="nav-item active"><span className="nav-icon">📁</span>Dossier</div>
        <div className="nav-item" onClick={() => navigate(`/pj/${dossier.id}`)}><span className="nav-icon">📎</span>PJ</div>
        <div className="nav-item" onClick={() => navigate(isSAV ? `/cr-sav/${dossier.id}` : isVT ? `/cr-vt/${dossier.id}` : `/cr/${dossier.id}`)}>
          <span className="nav-icon">📝</span>CR
        </div>
      </div>
    </div>
  );
}
